import React, { useEffect, useMemo, useState } from "react";
import {
  FiArrowUpRight,
  FiEye,
  FiFileText,
  FiSearch,
  FiX,
  FiCalendar,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import TopBanner from "../../../components/topBanner/topBanner";
import imgBanner from "../../../images/banner/banner---gioi-thieu.png";
import {
  getShareholderDocumentCategories,
  getShareholderDocuments,
} from "../../../apis/shareholderDocuments";
import { ASSET_BASE_URL } from "../../../constants/apiEndpoints";
import { normalizeLang } from "../../../utils/localeDataLoader";
import "./shareholderDocuments.scss";

const read = (item, key) =>
  item?.[key] ?? item?.[key[0].toUpperCase() + key.slice(1)];

const sectionCodes = {
  "tai-chinh": "finance",
  finance: "finance",
  "quan-tri-cong-ty": "corporate-governance",
  "corporate-governance": "corporate-governance",
  "bao-cao-thuong-nien": "annual-report",
  "annual-report": "annual-report",
  "bao-cao-quan-tri": "governance-report",
  "governance-report": "governance-report",
};

const resolveFileUrl = (url = "") => {
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `${ASSET_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
};

export default function ShareholderDocuments() {
  const { t, i18n } = useTranslation();
  const { section } = useParams();
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(true);

  // Filter & search states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const lang = normalizeLang(i18n.language);

    Promise.all([
      getShareholderDocumentCategories(lang),
      getShareholderDocuments(lang),
    ])
      .then(([categoryRows, documentRows]) => {
        if (!alive) return;
        const safeCategories = Array.isArray(categoryRows) ? categoryRows : [];
        const safeDocuments = Array.isArray(documentRows) ? documentRows : [];
        setCategories(safeCategories);
        setDocuments(safeDocuments);

        const requestedCode = sectionCodes[section];
        const requestedCategory = safeCategories.find(
          (row) => read(row, "code") === requestedCode,
        );
        setActive(
          requestedCategory
            ? String(read(requestedCategory, "id"))
            : ""
        );
      })
      .catch(() => {
        if (alive) {
          setCategories([]);
          setDocuments([]);
        }
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [i18n.language, section]);

  const currentCategory = useMemo(
    () => categories.find((cat) => String(read(cat, "id")) === active),
    [categories, active]
  );

  const displayTitle = currentCategory
    ? (() => {
        const text = read(currentCategory, "name") || read(currentCategory, "code") || "";
        const formatted = text.replace(/-/g, " ");
        return formatted.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      })()
    : t("shareholder_documents.fallback_title");

  const availableYears = useMemo(() => {
    const years = new Set();
    documents.forEach((item) => {
      const year = read(item, "publishedYear");
      if (year) years.add(String(year));
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [documents]);

  const shown = useMemo(() => {
    return documents.filter((item) => {
      // Category filter
      if (active && String(read(item, "categoryId")) !== active) {
        return false;
      }
      // Year filter
      if (selectedYear && String(read(item, "publishedYear")) !== selectedYear) {
        return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const title = (
          read(item, "title") ||
          read(item, "fileName") ||
          ""
        ).toLowerCase();
        const desc = (read(item, "description") || "").toLowerCase();
        const year = String(read(item, "publishedYear") || "");
        if (!title.includes(q) && !desc.includes(q) && !year.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [documents, active, selectedYear, searchQuery]);

  return (
    <div>
      <TopBanner
        titleCenter={t("shareholder_documents.title")}
        imgBanner={imgBanner}
        pageName={t("shareholder_documents.page_name")}
      />
      <main className="shareholder-documents layout_container">
        <header className="shareholder-header">
          <div className="shareholder-header__tag">
            <span>{t("shareholder_documents.hsv_corporate")}</span>
            {currentCategory && (
              <>
                <span className="shareholder-header__tag-dot">•</span>
                <span className="shareholder-header__tag-cat">
                  {displayTitle}
                </span>
              </>
            )}
          </div>
          <h1 className="shareholder-header__title">{displayTitle}</h1>
          <p className="shareholder-header__desc">
            {t("shareholder_documents.desc")}
          </p>
        </header>

        {/* Dribbble UI Filter and Search Bar */}
        <div className="shareholder-filter-bar">
          <div className="shareholder-filter-bar__search">
            <FiSearch
              className="shareholder-filter-bar__search-icon"
              aria-hidden="true"
            />
            <input
              type="text"
              className="shareholder-filter-bar__search-input"
              placeholder={t("shareholder_documents.search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="shareholder-filter-bar__search-clear"
                onClick={() => setSearchQuery("")}
                aria-label={t("shareholder_documents.clear_search")}
              >
                <FiX />
              </button>
            )}
          </div>

          <div className="shareholder-filter-bar__controls">
            {availableYears.length > 0 && (
              <div className="shareholder-filter-bar__select-wrapper">
                <FiCalendar
                  className="shareholder-filter-bar__select-icon"
                  aria-hidden="true"
                />
                <select
                  className="shareholder-filter-bar__select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  aria-label={t("shareholder_documents.filter_by_year")}
                >
                  <option value="">{t("shareholder_documents.all_years")}</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {t("shareholder_documents.year")} {year}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Document Results Summary Counter */}
        <div className="shareholder-summary">
          <div className="shareholder-summary__count">
            <span className="shareholder-summary__count-num">
              {shown.length}
            </span>
            <span className="shareholder-summary__count-text">
              {t("shareholder_documents.published_docs")}
            </span>
          </div>
          {(searchQuery || selectedYear) && (
            <button
              type="button"
              className="shareholder-summary__reset"
              onClick={() => {
                setSearchQuery("");
                setSelectedYear("");
              }}
            >
              <FiX /> {t("shareholder_documents.reset_filter")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="shareholder-state">
            <div className="shareholder-state__spinner" />
            <p>{t("shareholder_documents.loading")}</p>
          </div>
        ) : shown.length ? (
          <div className="shareholder-grid">
            {shown.map((item) => {
              const fileUrl = resolveFileUrl(read(item, "fileUrl"));
              const catId = String(read(item, "categoryId") || "");
              const itemCategory = categories.find(
                (c) => String(read(c, "id")) === catId
              );
              const catName = itemCategory
                ? read(itemCategory, "name") || read(itemCategory, "code")
                : null;

              return (
                <article key={read(item, "id")} className="shareholder-card">
                  <div className="shareholder-card__main">
                    <div className="shareholder-card__icon">
                      <FiFileText />
                    </div>
                    <div className="shareholder-card__info">
                      <div className="shareholder-card__meta">
                        {read(item, "publishedYear") && (
                          <span className="shareholder-card__year">
                            {read(item, "publishedYear")}
                          </span>
                        )}
                        {catName && !currentCategory && (
                          <span className="shareholder-card__cat">
                            {catName}
                          </span>
                        )}
                      </div>
                      <h2>{read(item, "title") || read(item, "fileName")}</h2>
                      {read(item, "description") && (
                        <p>{read(item, "description")}</p>
                      )}
                    </div>
                  </div>
                  <div className="shareholder-card__action">
                    {fileUrl ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shareholder-card__btn"
                      >
                        <FiEye aria-hidden="true" />
                        <span>{t("shareholder_documents.view_doc")}</span>
                        <FiArrowUpRight aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="shareholder-card__unavailable">
                        {t("shareholder_documents.updating_file")}
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="shareholder-state">
            <div className="shareholder-state__icon-box">
              <FiFileText />
            </div>
            <strong>{t("shareholder_documents.not_found")}</strong>
            <p>
              {searchQuery || selectedYear
                ? t("shareholder_documents.try_search_again")
                : t("shareholder_documents.soon_published")}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
